import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { nodeKey } from '@nw/contracts';
import { Instrument, Readout } from '@nw/ui';
import { useEnabledWebModules, useEntityHref, useFormat, useHome, useMe, usePreferences, type WidgetDef, type WidgetSize } from '@nw/web-platform';
import { allSpaces, useSpaceMemory, type Space } from '../shell/spaces.js';

function ClockWidget() {
  const fmt = useFormat();
  const me = useMe();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(t);
  }, []);
  return (
    <Instrument
      label="Local time"
      status={
        <>
          <span className="nw-live-dot" /> Live
        </>
      }
    >
      <Readout className="mt-auto" value={fmt.time(now)} />
      <p className="text-sm text-[var(--nw-quiet)]">{fmt.today(now)}</p>
      <p className="text-xs text-[var(--nw-quiet)]">{me.data?.workspace.timezone.replace(/_/g, ' ')}</p>
    </Instrument>
  );
}

const VERBS: Record<string, string> = {
  added: 'Added',
  updated: 'Edited',
  read: 'Read',
  finished: 'Finished',
  noted: 'Wrote',
  connected: 'Connected',
  attached_pdf: 'Attached a PDF to',
  saved_view: 'Saved',
  deleted: 'Removed',
};

function ActivityWidget({ size }: { size: string }) {
  const home = useHome();
  const fmt = useFormat();
  const items = (home.data?.activity ?? []).filter((a, i, all) => i === 0 || !(all[i - 1]!.verb === a.verb && all[i - 1]!.entity.key === a.entity.key));
  return (
    <Instrument label="Activity">
      {items.length ? (
        <ol className={size === 'w' ? 'mt-3 grid grid-cols-2 gap-x-6 gap-y-2 lg:grid-cols-3' : 'mt-3 grid gap-2'}>
          {items.slice(0, size === 'w' ? 6 : 7).map((a) => (
            <li key={a.id} className="flex min-w-0 items-baseline gap-2 text-sm">
              <span className="nw-live-dot shrink-0 self-center" />
              <span className="min-w-0 truncate">
                <span className="text-[var(--nw-quiet)]">{VERBS[a.verb] ?? a.verb} </span>
                <Link to={a.entity.href} className="font-medium hover:underline">
                  {a.entity.title}
                </Link>
              </span>
              <time dateTime={a.occurredAt} className="ms-auto shrink-0 text-xs text-[var(--nw-quiet)] tabular-nums">
                {fmt.relative(a.occurredAt)}
              </time>
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-auto text-sm text-[var(--nw-quiet)]">What you add, read and connect shows up here.</p>
      )}
    </Instrument>
  );
}

function PinnedWidget() {
  const prefs = usePreferences();
  const hrefFor = useEntityHref();
  const pins = prefs.data?.pinnedRefs ?? [];
  return (
    <Instrument label="Pinned">
      {pins.length ? (
        <ul className="mt-2 grid gap-1.5">
          {pins.slice(0, 4).map((p) => {
            const href = hrefFor(p.ref);
            return (
              <li key={nodeKey(p.ref)} className="truncate text-sm">
                {href ? (
                  <Link to={href} className="hover:underline">
                    {p.label}
                  </Link>
                ) : (
                  p.label
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-auto text-sm text-[var(--nw-quiet)]">Pin a book, idea or person from its window.</p>
      )}
    </Instrument>
  );
}

function SpaceTileStatus({ space }: { space: Space }) {
  const status = space.module?.useStatus?.() ?? null;
  return <span className="nw-mini-status">{status ?? space.description ?? ''}</span>;
}

/** Every module as a tile with its live status: the control centre. Grows with each new module. */
function SpacesWidget({ size }: { size: WidgetSize }) {
  const modules = useEnabledWebModules();
  const last = useSpaceMemory((s) => s.last);
  const spaces = allSpaces(modules).filter((s) => s.id !== 'home');
  const max = size === 'l' ? 8 : size === 'w' ? 8 : 4;
  return (
    <Instrument label="Spaces">
      <ul className={size === 'w' ? 'nw-mini-grid nw-mini-grid-w' : 'nw-mini-grid'}>
        {spaces.slice(0, max).map((s) => (
          <li key={s.id}>
            <Link to={last[s.id] ?? s.path} className="nw-mini-tile" style={{ ['--h' as string]: s.hue }} data-mode={s.mode}>
              <span className="nw-mini-icon">
                <s.icon className="size-5" />
              </span>
              <span className="nw-mini-label">{s.label}</span>
              <SpaceTileStatus space={s} />
            </Link>
          </li>
        ))}
      </ul>
      {!spaces.length ? <p className="mt-auto text-sm text-[var(--nw-quiet)]">Turn on a module in Settings to see it here.</p> : null}
    </Instrument>
  );
}

export const coreWidgets: WidgetDef[] = [
  {
    id: 'core.clock',
    title: 'Local time',
    description: 'Time and date in your workspace zone',
    sizes: ['s'],
    defaultSize: 's',
    Component: ClockWidget,
  },
  {
    id: 'core.activity',
    title: 'Activity',
    description: 'What happened recently',
    sizes: ['m', 'l', 'w'],
    defaultSize: 'w',
    Component: ActivityWidget,
  },
  {
    id: 'core.pinned',
    title: 'Pinned',
    description: 'Things you pinned',
    sizes: ['s', 'm'],
    defaultSize: 'm',
    Component: PinnedWidget,
  },
  {
    id: 'core.spaces',
    title: 'Spaces',
    description: 'Every module with its live status',
    sizes: ['m', 'l', 'w'],
    defaultSize: 'm',
    Component: SpacesWidget,
  },
];

export const DEFAULT_LAYOUT = ['core.clock', 'core.spaces', 'library.shelf', 'library.reading', 'mind.network', 'mind.thought', 'library.note', 'core.activity'];
